/**
 * Button Component
 * 
 * A versatile button with multiple variants and sizes.
 * 
 * @module components/ui/Button
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

// =============================================================================
// Variants
// =============================================================================

const buttonVariants = cva(
  // Base styles
  `inline-flex items-center justify-center gap-2 font-medium rounded-lg
   transition-all duration-150 
   focus:outline-none focus:ring-2 focus:ring-inset
   disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none`,
  {
    variants: {
      variant: {
        primary: `bg-brand-500 text-white 
                  hover:bg-brand-600 
                  focus:ring-brand-500
                  active:bg-brand-700`,
        secondary: `bg-white text-gray-700 border border-gray-300
                    hover:bg-gray-50 hover:border-gray-400
                    focus:ring-gray-500
                    active:bg-gray-100`,
        ghost: `bg-transparent text-gray-600
                hover:bg-gray-100 hover:text-gray-900
                focus:ring-gray-500`,
        danger: `bg-error-500 text-white
                 hover:bg-error-600
                 focus:ring-error-500`,
        link: `bg-transparent text-brand-600 underline-offset-4
               hover:underline hover:text-brand-700
               focus:ring-brand-500 p-0 h-auto`,
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
        'icon-sm': 'h-8 w-8',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
);

// =============================================================================
// Component
// =============================================================================

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Show loading spinner */
  loading?: boolean;
  /** Icon to show before text */
  leftIcon?: React.ReactNode;
  /** Icon to show after text */
  rightIcon?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      loading = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, fullWidth }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : leftIcon ? (
          leftIcon
        ) : null}
        {children}
        {rightIcon && !loading && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { buttonVariants };
